import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Chip, CircularProgress, Paper, Switch, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { collection, doc, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';

/**
 * Beheert wie administrator van het spel is. De rol staat als `role` op het
 * gebruikersdocument; firestore.rules leest hem via isAdmin().
 *
 * Je kunt je eigen rol niet afnemen — anders sluit de laatste administrator
 * zichzelf buiten en is er niemand meer die het terug kan draaien.
 */
function AdminBeheerders() {
    const { user } = useAuth();
    const notify = useNotification();
    const [users, setUsers] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [savingUid, setSavingUid] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const snapshot = await getDocs(collection(db, 'users'));
            const list = snapshot.docs
                .map((d) => ({ uid: d.id, ...d.data() }))
                .sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''));
            setUsers(list);
        } catch (err) {
            console.error('Kon gebruikers niet ophalen:', err);
            notify.error('De gebruikers konden niet opgehaald worden.');
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => { load(); }, [load]);

    const handleToggle = async (target, shouldBeAdmin) => {
        setSavingUid(target.uid);
        const nextRole = shouldBeAdmin ? 'admin' : 'participant';
        try {
            await updateDoc(doc(db, 'users', target.uid), { role: nextRole });
            setUsers((current) => current.map((u) => (
                u.uid === target.uid ? { ...u, role: nextRole } : u
            )));
        } catch (err) {
            console.error('Kon rol niet wijzigen:', err);
            notify.error(`De rol van ${target.name || target.email} kon niet gewijzigd worden.`);
        } finally {
            setSavingUid(null);
        }
    };

    const term = search.trim().toLowerCase();
    const visible = term
        ? users.filter((u) => `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(term))
        : users;
    const adminCount = users.filter((u) => u.role === 'admin').length;

    return (
        <Box>
            <Typography variant="h4">Beheerders</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
                Administrators beheren seizoenen, stamgegevens en de puntentelling van het hele spel.
                Beheerders van een losse subleague wijs je aan binnen die subleague.
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <TextField
                    size="small"
                    label="Zoek op naam of e-mail"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ minWidth: 280 }}
                />
                <Chip label={`${adminCount} administrator${adminCount === 1 ? '' : 's'}`} variant="outlined" />
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Gebruiker</TableCell>
                                <TableCell>E-mail</TableCell>
                                <TableCell align="right" sx={{ width: 160 }}>Administrator</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visible.map((target) => {
                                const isSelf = target.uid === user?.uid;
                                const isAdmin = target.role === 'admin';
                                return (
                                    <TableRow key={target.uid} hover>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {target.name || '(geen naam)'}
                                                {isSelf && (
                                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                        jij
                                                    </Typography>
                                                )}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">{target.email}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Switch
                                                checked={isAdmin}
                                                disabled={isSelf || savingUid === target.uid}
                                                onChange={(e) => handleToggle(target, e.target.checked)}
                                                inputProps={{ 'aria-label': `Administrator voor ${target.email}` }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {visible.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3}>
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                                            Geen gebruikers gevonden.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}

export default AdminBeheerders;
